import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Info, Loader2, Swords } from 'lucide-react';

import { env } from '@/env';
import { useGetMyMatchesQuery, useGetMeQuery } from '@/services/qgammonApi';
import { useResolveProfilesBySubsQuery } from '@/services/socialApi';
import type { QgammonMatchSummary } from '@/types/qgammon';
import { useHaptics } from '@/hooks/useHaptics';
import { PanelWaiting } from '@/components/hub/PanelNotice';
import { useQgammonRitmo, useQgammonVerdetto } from '@/hooks/useQgammonLista';
import { RigaStorico } from '@/components/hub/panels/storico/RigaStorico';
import { LegendaIcone } from '@/components/hub/panels/storico/IconeAvversario';
import { StatoAvversariProvider } from '@/components/hub/panels/storico/StatoAvversari';
import { useStatoAvversari } from '@/components/hub/panels/storico/contestoStato';
import { iconeDi, rigaConNomeSocial, rigaStorico, subsDaRisolvere, type StatoSocial } from '@/lib/qgammon/storicoPartite';

/**
 * Hub "Partite" section for Backgammon: the caller's match history + replay.
 *
 * I cinque casi e il polling: `useQgammonLista`. Le frasi di errore e di vuoto restano quelle di
 * prima — la scadenza dei 45 s cambia QUANDO compare l'errore, mai QUALE frase e'.
 *
 * La riga (tavola a2, L-2604) e le sue icone social: `panels/storico/` e `lib/qgammon/storicoPartite.ts`.
 * Reference nel KB: `services/qlash-telegram-app/ui/2026-09-30-storico-backgammon-proposte.md`.
 */
export function QgammonMatchesPanel() {
  const { t } = useTranslation('backgammon');
  const { data: me } = useGetMeQuery();
  const myId = me?.user.id ?? null;
  const ritmo = useQgammonRitmo();
  const q = useGetMyMatchesQuery(undefined, ritmo.opts);
  const rows = q.data?.matches ?? [];
  const verdict = useQgammonVerdetto(ritmo, q, rows.length);
  // La legenda delle icone compare solo se almeno una riga (di una pagina qualunque) ne porta: la dice una pagina.
  const [haIcone, setHaIcone] = useState(false);

  if (verdict === 'loading' || verdict === 'slow') {
    return <PanelWaiting slow={verdict === 'slow'} />;
  }

  return (
    <StatoAvversariProvider>
      <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto py-2">
        {(verdict === 'error' || verdict === 'unavailable') && (
          <p className="rounded-xl bg-bg-secondary p-4 text-center text-sm text-danger">
            {t('hubPanels.matchesError', {
              defaultValue: 'Could not load your matches. Sign in again if this persists.',
            })}
          </p>
        )}
        {verdict === 'empty' && (
          <div className="flex flex-col items-center gap-3 rounded-xl bg-bg-secondary p-6 text-center">
            <Swords aria-hidden className="h-6 w-6 text-fg-muted" />
            <p className="text-sm text-fg-muted">
              {t('hubPanels.matchesEmpty', { defaultValue: 'No matches yet — play your first game!' })}
            </p>
          </div>
        )}
        {rows.length > 0 && (
          // UNA volta in testa alla lista, non su ogni riga. ⚠️ La frase e' una semplificazione: «errore medio per
          // mossa» va confermata con qgammon-orcr (KB, «Le parole»).
          <p data-testid="storico-legenda-pr" className="flex items-center gap-2 px-1 text-xs text-fg-muted">
            <Info aria-hidden className="h-3.5 w-3.5 shrink-0" />
            <span>
              <span className="font-bold text-fg">{t('hubPanels.pr')}</span>: {t('storico.legendaPr')}
            </span>
          </p>
        )}
        <PaginaStorico
          partite={rows}
          myId={myId}
          onHaIcone={() => setHaIcone(true)}
          cursorSuccessivo={q.data?.nextCursor ?? null}
        />
        {haIcone && <LegendaDelleIcone />}
      </div>
    </StatoAvversariProvider>
  );
}

/** In fondo alla lista. La riga «Sfida» segue la serratura letta UNA volta dal provider: la legenda non la rilegge. */
function LegendaDelleIcone() {
  return <LegendaIcone conSfida={useStatoAvversari().sfidaAccesa} />;
}

/**
 * Una pagina dello storico: le sue righe, UN lotto QSocials per i suoi avversari (sub deduplicati), e il «Mostra altre»
 * che apre la pagina dopo. Lo stato social di un avversario sta nel provider, una sola volta per `sub`: il lotto dice
 * com'era alla lettura, un gesto lo cambia per TUTTE le righe, di qualunque pagina.
 */
function PaginaStorico({
  partite,
  myId,
  onHaIcone,
  cursorSuccessivo,
}: {
  partite: QgammonMatchSummary[];
  myId: string | null;
  onHaIcone: () => void;
  cursorSuccessivo: string | null;
}) {
  const stato = useStatoAvversari();
  const righe = useMemo(() => partite.map((m) => ({ id: m.id, riga: rigaStorico(m, myId) })), [partite, myId]);
  const subs = useMemo(() => subsDaRisolvere(righe.map((r) => r.riga)), [righe]);
  // A QSocials spento (build) o senza avversari da risolvere non parte niente. Un lotto fallito o un 404 (server
  // vecchio) = nessuna icona: il nome di gioco resta, e la lista e' usabile com'era.
  const lotto = useResolveProfilesBySubsQuery(
    { subs },
    // Il lotto dice com'e' ADESSO: tornare sulla scheda lo rilegge, o un gesto fatto prima resterebbe indietro.
    { skip: subs.length === 0 || !env.featureSocialEnabled, refetchOnMountOrArgChange: true },
  );
  const dalLotto = useMemo(() => {
    const m = new Map<string, StatoSocial>();
    if (lotto.isError) return m;
    for (const r of lotto.data ?? []) m.set(r.sub, { relation: r.relation, following: r.following });
    return m;
  }, [lotto.data, lotto.isError]);
  // Il nome QSocials per sub (I-282, L-2636): sta accanto allo stato social e segue la stessa regola — lotto fallito o
  // 404 = mappa vuota, e vince il nome di QGammon. Un `displayName` null o vuoto lo scarta `nomeAvversario`, non qui.
  const nomiDalLotto = useMemo(() => {
    const m = new Map<string, string | null>();
    if (lotto.isError) return m;
    for (const r of lotto.data ?? []) m.set(r.sub, r.displayName ?? null);
    return m;
  }, [lotto.data, lotto.isError]);

  const adesso = new Date();
  const conIcone = righe.some(
    (r) =>
      r.riga.conIcone &&
      r.riga.avversario.sub !== null &&
      iconeDi(stato.statoDi(r.riga.avversario.sub, dalLotto.get(r.riga.avversario.sub) ?? null), stato.sfidaAccesa).length > 0,
  );
  useEffect(() => {
    if (conIcone) onHaIcone();
  }, [conIcone, onHaIcone]);

  return (
    <>
      {righe.map(({ id, riga }) => (
        <RigaStorico
          key={id}
          id={id}
          riga={rigaConNomeSocial(riga, riga.avversario.sub ? nomiDalLotto.get(riga.avversario.sub) : null)}
          dalLotto={riga.avversario.sub ? (dalLotto.get(riga.avversario.sub) ?? null) : null}
          adesso={adesso}
        />
      ))}
      <MostraAltre cursor={cursorSuccessivo} myId={myId} onHaIcone={onHaIcone} />
    </>
  );
}

function MostraAltre({ cursor, myId, onHaIcone }: { cursor: string | null; myId: string | null; onHaIcone: () => void }) {
  const { t } = useTranslation('backgammon');
  const haptics = useHaptics();
  // Aperto resta aperto: se la prima pagina, sotto polling, ha un cursore nuovo (e' arrivata una partita), la pagina dopo
  // si RILEGGE col cursore nuovo e non si richiude sotto il dito di chi sta scorrendo.
  const [aperto, setAperto] = useState(false);
  if (cursor === null) return null;
  if (!aperto) {
    return (
      <button
        type="button"
        data-testid="storico-mostra-altre"
        onClick={() => {
          haptics.selection();
          setAperto(true);
        }}
        className="mx-auto mt-1 rounded-full bg-bg-secondary px-4 py-2 text-sm font-semibold text-fg transition-opacity active:opacity-80"
      >
        {t('storico.mostraAltre')}
      </button>
    );
  }
  return <PaginaSuccessiva cursor={cursor} myId={myId} onHaIcone={onHaIcone} />;
}

/**
 * Le partite dopo `cursor`: una lettura sola (nessun polling: e' storia), con i suoi tre stati. Cambiando `cursor` riparte la
 * lettura, ma `q.data` tiene l'ultima risposta buona: le righe non spariscono mentre arriva la nuova.
 */
function PaginaSuccessiva({ cursor, myId, onHaIcone }: { cursor: string; myId: string | null; onHaIcone: () => void }) {
  const { t } = useTranslation('backgammon');
  const q = useGetMyMatchesQuery({ cursor });
  if (q.data) {
    return <PaginaStorico partite={q.data.matches} myId={myId} onHaIcone={onHaIcone} cursorSuccessivo={q.data.nextCursor ?? null} />;
  }
  if (q.isError) {
    return (
      <div className="flex flex-col items-center gap-2 rounded-xl bg-bg-secondary p-4 text-center">
        <p role="alert" className="text-sm text-danger">
          {t('storico.altreErrore')}
        </p>
        <button
          type="button"
          onClick={() => void q.refetch()}
          className="rounded-full bg-bg-tertiary px-4 py-1.5 text-sm font-semibold text-fg"
        >
          {t('storico.riprova')}
        </button>
      </div>
    );
  }
  return (
    <div className="flex justify-center py-3" role="status" aria-label={t('storico.carico')}>
      <Loader2 aria-hidden className="h-5 w-5 animate-spin text-fg-muted" />
    </div>
  );
}
